import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { smsAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/numberFormat';

// Service color themes
const SERVICE_THEMES = {
  messaging: {
    primary: '#4338ca',
    primaryLight: 'rgba(99, 102, 241, 0.1)',
    primaryMedium: 'rgba(99, 102, 241, 0.14)',
    primaryFaint: 'rgba(99, 102, 241, 0.06)',
    gradient: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
    gradientSoft: 'linear-gradient(135deg, rgba(99, 102, 241, 0.14) 0%, rgba(6, 182, 212, 0.06) 100%)',
    accent: '#06b6d4',
    accentGradient: 'linear-gradient(180deg, #4338ca 0%, #06b6d4 100%)',
    dotGradient: 'linear-gradient(135deg, #4338ca, #06b6d4)',
    dotShadow: '0 0 8px rgba(67, 56, 202, 0.5)',
    borderLine: 'rgba(99, 102, 241, 0.2)',
    sectionBg: 'rgba(99, 102, 241, 0.1)',
    label: 'Messaging',
    icon: 'MessageSquare',
  },
  whatsapp: {
    primary: '#128C7E',
    primaryLight: 'rgba(37, 211, 102, 0.1)',
    primaryMedium: 'rgba(37, 211, 102, 0.14)',
    primaryFaint: 'rgba(37, 211, 102, 0.06)',
    gradient: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
    gradientSoft: 'linear-gradient(135deg, rgba(37, 211, 102, 0.14) 0%, rgba(18, 140, 126, 0.06) 100%)',
    accent: '#128C7E',
    accentGradient: 'linear-gradient(180deg, #25D366 0%, #128C7E 100%)',
    dotGradient: 'linear-gradient(135deg, #25D366, #128C7E)',
    dotShadow: '0 0 8px rgba(18, 140, 126, 0.5)',
    borderLine: 'rgba(37, 211, 102, 0.22)',
    sectionBg: 'rgba(37, 211, 102, 0.08)',
    label: 'WhatsApp Business',
    icon: 'MessageCircle',
  },
  rcs: {
    primary: '#1a73e8',
    primaryLight: 'rgba(66, 133, 244, 0.1)',
    primaryMedium: 'rgba(66, 133, 244, 0.14)',
    primaryFaint: 'rgba(66, 133, 244, 0.06)',
    gradient: 'linear-gradient(135deg, #1a73e8 0%, #34A853 100%)',
    gradientSoft: 'linear-gradient(135deg, rgba(66, 133, 244, 0.14) 0%, rgba(52, 168, 83, 0.06) 100%)',
    accent: '#34A853',
    accentGradient: 'linear-gradient(180deg, #1a73e8 0%, #34A853 100%)',
    dotGradient: 'linear-gradient(135deg, #1a73e8, #34A853)',
    dotShadow: '0 0 8px rgba(26, 115, 232, 0.5)',
    borderLine: 'rgba(66, 133, 244, 0.22)',
    sectionBg: 'rgba(66, 133, 244, 0.08)',
    label: 'Google RCS',
    icon: 'Smartphone',
  },
  email: {
    primary: '#b45309',
    primaryLight: 'rgba(245, 158, 11, 0.1)',
    primaryMedium: 'rgba(245, 158, 11, 0.14)',
    primaryFaint: 'rgba(245, 158, 11, 0.06)',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #b45309 100%)',
    gradientSoft: 'linear-gradient(135deg, rgba(245, 158, 11, 0.14) 0%, rgba(180, 83, 9, 0.06) 100%)',
    accent: '#b45309',
    accentGradient: 'linear-gradient(180deg, #F59E0B 0%, #b45309 100%)',
    dotGradient: 'linear-gradient(135deg, #F59E0B, #b45309)',
    dotShadow: '0 0 8px rgba(180, 83, 9, 0.5)',
    borderLine: 'rgba(245, 158, 11, 0.22)',
    sectionBg: 'rgba(245, 158, 11, 0.08)',
    label: 'Bulk Email',
    icon: 'Mail',
  },
  global: {
    primary: '#4338ca',
    primaryLight: 'rgba(99, 102, 241, 0.1)',
    primaryMedium: 'rgba(99, 102, 241, 0.14)',
    primaryFaint: 'rgba(99, 102, 241, 0.06)',
    gradient: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
    gradientSoft: 'linear-gradient(135deg, rgba(99, 102, 241, 0.14) 0%, rgba(6, 182, 212, 0.06) 100%)',
    accent: '#06b6d4',
    accentGradient: 'linear-gradient(180deg, #4338ca 0%, #06b6d4 100%)',
    dotGradient: 'linear-gradient(135deg, #4338ca, #06b6d4)',
    dotShadow: '0 0 8px rgba(67, 56, 202, 0.5)',
    borderLine: 'rgba(99, 102, 241, 0.18)',
    sectionBg: 'rgba(99, 102, 241, 0.04)',
    label: 'Global',
    icon: 'Globe',
  },
};

const ServiceSidebar = ({ serviceNavigation, sidebarOpen, setSidebarOpen, user, logout }) => {
  const location = useLocation();
  const [openSuperMenu, setOpenSuperMenu] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  const handleMenuClick = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const isSelected = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isSubmenuActive = (children) => {
    return children?.some(child => {
      if (child.children) return isSubmenuActive(child.children);
      return isSelected(child.href);
    });
  };

  const isSuperMenuActive = (service) => {
    if (!service.items) return false;
    return service.items.some(item => {
      if (item.children) return isSubmenuActive(item.children);
      if (item.href) return isSelected(item.href);
      return false;
    });
  };

  const toggleSuperMenu = (serviceKey) => {
    if (openSuperMenu === serviceKey) {
      setOpenSuperMenu(null);
      setOpenSubmenu(null);
    } else {
      setOpenSuperMenu(serviceKey);
      setOpenSubmenu(null);
    }
  };

  const toggleSubmenu = (menuKey) => {
    setOpenSubmenu(openSubmenu === menuKey ? null : menuKey);
  };

  // Auto-open super menu and submenu based on current path
  // Default to 'messaging' when on dashboard or no match found
  useEffect(() => {
    for (const service of serviceNavigation) {
      if (service.type === 'link') {
        continue;
      }
      if (service.items) {
        for (const item of service.items) {
          if (item.href && isSelected(item.href)) {
            setOpenSuperMenu(service.key);
            return;
          }
          if (item.children) {
            for (const child of item.children) {
              if (child.href && isSelected(child.href)) {
                setOpenSuperMenu(service.key);
                setOpenSubmenu(item.menuKey);
                return;
              }
            }
          }
        }
      }
    }
    // No service group matched — default to Messaging expanded
    const hasMessaging = serviceNavigation.some(s => s.key === 'messaging');
    if (hasMessaging) {
      setOpenSuperMenu('messaging');
    }
  }, [location.pathname]);

  // Fetch balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await smsAPI.getBalanceWithSMS();
        const balanceInfo = response.data?.data || response.data;
        setBalanceData(balanceInfo);
      } catch (error) {
        console.error('Failed to fetch balance:', error);
      }
    };
    if (user?.role === 'user' || user?.role === 'USER') {
      fetchBalance();
    }
  }, [user]);

  const handleBalanceClick = async () => {
    if (balanceRefreshing) return;
    setBalanceRefreshing(true);
    try {
      const response = await smsAPI.refreshBalance();
      const balanceInfo = response.data?.data || response.data;
      setBalanceData(balanceInfo);
    } catch (error) {
      console.error('Failed to refresh balance:', error);
    } finally {
      setBalanceRefreshing(false);
    }
  };

  const getIcon = (iconName, size = 16) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent style={{ width: `${size}px`, height: `${size}px` }} /> : <Icons.Circle style={{ width: `${size}px`, height: `${size}px` }} />;
  };

  const getTheme = (serviceKey) => SERVICE_THEMES[serviceKey] || SERVICE_THEMES.global;

  // Render a submenu item (leaf link)
  const renderLeafItem = (child, theme) => {
    const childActive = isSelected(child.href);
    const childHovered = hoveredItem === child.href;
    return (
      <Link
        key={child.name}
        to={child.href}
        onClick={handleMenuClick}
        onMouseEnter={() => setHoveredItem(child.href)}
        onMouseLeave={() => setHoveredItem(null)}
        className="sidebar-item submenu-enter group relative flex items-center gap-2 pl-3 pr-2.5 rounded-md"
        style={{
          height: '32px',
          color: childActive ? theme.primary : childHovered ? '#1e1b4b' : '#4b5563',
          background: childActive ? theme.gradientSoft : childHovered ? theme.primaryFaint : 'transparent',
          fontWeight: childActive ? 650 : 450,
          fontSize: '12.5px',
          lineHeight: '1.4',
          boxShadow: childActive ? `inset 0 0 0 1px ${theme.primary}12` : 'none',
        }}
      >
        {childActive && (
          <div className="absolute -left-[11px] top-1/2 -translate-y-1/2 w-[5px] h-[5px] rounded-full" style={{
            background: theme.dotGradient,
            boxShadow: theme.dotShadow,
          }} />
        )}
        <div className="flex-shrink-0 transition-all duration-150" style={{
          color: childActive ? theme.primary : childHovered ? theme.primary : '#9ca3af',
          transform: childActive ? 'scale(1.1)' : 'scale(1)',
        }}>
          {getIcon(child.icon, 13)}
        </div>
        <span className="truncate" style={{ letterSpacing: '0.01em' }}>{child.name}</span>
        {child.badge && (
          <span className="ml-auto px-1.5 py-0.5 text-[8px] font-bold text-white rounded-md flex-shrink-0" style={{
            background: theme.gradient,
            lineHeight: '1',
          }}>
            {child.badge}
          </span>
        )}
      </Link>
    );
  };

  // Render a menu group (with children)
  const renderMenuGroup = (item, theme) => {
    const isOpen = openSubmenu === item.menuKey;
    const hasActiveChild = isSubmenuActive(item.children);
    const isHovered = hoveredItem === `group-${item.menuKey}`;

    return (
      <div key={item.name} className="mb-0.5">
        <button
          onClick={() => toggleSubmenu(item.menuKey)}
          onMouseEnter={() => setHoveredItem(`group-${item.menuKey}`)}
          onMouseLeave={() => setHoveredItem(null)}
          className="sidebar-item w-full group relative flex items-center gap-2 px-2.5 rounded-md font-medium"
          style={{
            height: '34px',
            color: isOpen || hasActiveChild ? theme.primary : isHovered ? '#1e1b4b' : '#1f2937',
            background: isOpen || hasActiveChild ? theme.primaryLight : isHovered ? theme.primaryFaint : 'transparent',
            fontSize: '13px',
            lineHeight: '1.4',
            fontWeight: hasActiveChild ? 600 : 500,
          }}
        >
          {hasActiveChild && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] rounded-r-full" style={{
              height: '14px',
              background: theme.accentGradient,
            }} />
          )}
          <div className="flex-shrink-0 w-[24px] h-[24px] rounded-md flex items-center justify-center transition-all duration-150" style={{
            background: isOpen || hasActiveChild ? theme.gradient : isHovered ? theme.primaryMedium : theme.primaryFaint,
            color: isOpen || hasActiveChild ? '#fff' : isHovered ? theme.primary : '#9ca3af',
            boxShadow: isOpen || hasActiveChild ? `0 2px 6px ${theme.primary}25` : 'none',
          }}>
            {getIcon(item.icon, 13)}
          </div>
          <span className="truncate flex-1 text-left" style={{ letterSpacing: '0.01em' }}>{item.name}</span>
          <Icons.ChevronRight className="w-3 h-3 flex-shrink-0 transition-transform duration-200" style={{
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            opacity: 0.35,
          }} />
        </button>

        <div style={{
          maxHeight: isOpen ? `${item.children.length * 34 + 6}px` : '0px',
          overflow: 'hidden',
          transition: 'max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <div className="py-0.5 ml-[14px] pl-2.5" style={{ borderLeft: `1.5px solid ${theme.borderLine}` }}>
            {item.children.map((child) => renderLeafItem(child, theme))}
          </div>
        </div>
      </div>
    );
  };

  // Render a direct link item inside a service
  const renderDirectItem = (item, theme) => {
    const active = isSelected(item.href);
    const isHovered = hoveredItem === item.href;
    return (
      <div key={item.name} className="mb-0.5">
        <Link
          to={item.href}
          onClick={handleMenuClick}
          onMouseEnter={() => setHoveredItem(item.href)}
          onMouseLeave={() => setHoveredItem(null)}
          className="sidebar-item group relative flex items-center gap-2 px-2.5 rounded-md"
          style={{
            height: '34px',
            color: active ? theme.primary : isHovered ? '#1e1b4b' : '#1f2937',
            background: active ? theme.gradientSoft : isHovered ? theme.primaryFaint : 'transparent',
            fontWeight: active ? 650 : 500,
            fontSize: '13px',
            lineHeight: '1.4',
          }}
        >
          {active && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] rounded-r-full" style={{
              height: '14px',
              background: theme.accentGradient,
            }} />
          )}
          <div className="flex-shrink-0 w-[24px] h-[24px] rounded-md flex items-center justify-center transition-all duration-150" style={{
            background: active ? theme.gradient : isHovered ? theme.primaryMedium : theme.primaryFaint,
            color: active ? '#fff' : isHovered ? theme.primary : '#9ca3af',
            boxShadow: active ? `0 2px 6px ${theme.primary}25` : 'none',
          }}>
            {getIcon(item.icon, 13)}
          </div>
          <span className="truncate flex-1" style={{ letterSpacing: '0.01em' }}>{item.name}</span>
          {item.badge && (
            <span className="px-1.5 py-0.5 text-[8px] font-bold text-white rounded-md flex-shrink-0" style={{
              background: theme.gradient,
              lineHeight: '1',
            }}>
              {item.badge}
            </span>
          )}
        </Link>
      </div>
    );
  };

  // Section divider with label
  const renderSectionDivider = (label, theme) => (
    <div className="pt-4 pb-2 px-3 flex items-center gap-2">
      <div style={{ height: '1.5px', flex: 1, background: `linear-gradient(90deg, ${theme.primary}30 0%, transparent 100%)` }} />
      <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] flex-shrink-0 px-1.5" style={{ color: theme.primary, textShadow: `0 0 20px ${theme.primary}20` }}>{label}</span>
      <div style={{ height: '1.5px', flex: 1, background: `linear-gradient(90deg, transparent 0%, ${theme.primary}30 100%)` }} />
    </div>
  );

  return (
    <>
      <style>{`
        .sidebar-nav { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility; }
        .sidebar-nav::-webkit-scrollbar { width: 4px; }
        .sidebar-nav::-webkit-scrollbar-track { background: transparent; }
        .sidebar-nav::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.12); border-radius: 10px; }
        .sidebar-nav::-webkit-scrollbar-thumb:hover { background: rgba(99, 102, 241, 0.2); }
        .sidebar-item { transition: all 0.15s ease; letter-spacing: 0.01em; }
        .sidebar-item:active { transform: scale(0.985); }
        .sidebar-item:hover { transform: translateX(1px); }
        .submenu-enter { animation: submenuSlide 0.15s ease-out; }
        @keyframes submenuSlide {
          from { opacity: 0; transform: translateX(-4px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .super-menu-content { transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease; }
        .service-section { border-radius: 10px; margin-bottom: 4px; }
      `}</style>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 bottom-0 z-40 transition-transform duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          top: '62px',
          width: sidebarOpen && window.innerWidth < 1024 ? '280px' : '256px',
          background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRight: '1px solid #e8eaed',
          boxShadow: '2px 0 12px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Mobile close */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #e8eaed' }}>
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Navigation</span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-150"
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icons.X className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="sidebar-nav flex-1 px-2 py-2.5 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(99,102,241,0.12) transparent' }}>
            {serviceNavigation.map((service, serviceIndex) => {
              const prevService = serviceIndex > 0 ? serviceNavigation[serviceIndex - 1] : null;
              const theme = getTheme(service.themeKey || 'global');

              // Top-level link (Dashboard, API Management, Settings)
              if (service.type === 'link') {
                const active = isSelected(service.href);
                const isHovered = hoveredItem === service.href;

                // Show section divider before global links that come after service groups
                const needsDivider = prevService && prevService.type !== 'link';

                return (
                  <React.Fragment key={service.key}>
                    {needsDivider && renderSectionDivider('Global', getTheme('global'))}
                    <div className="mb-0.5">
                      <Link
                        to={service.href}
                        onClick={handleMenuClick}
                        onMouseEnter={() => setHoveredItem(service.href)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="sidebar-item group relative flex items-center gap-2.5 px-2.5 rounded-lg"
                        style={{
                          height: '38px',
                          color: active ? theme.primary : isHovered ? '#1e1b4b' : '#1e293b',
                          background: active ? theme.gradientSoft : isHovered ? theme.primaryFaint : 'transparent',
                          fontWeight: active ? 600 : 500,
                          fontSize: '13.5px',
                          lineHeight: '1.4',
                        }}
                      >
                        {active && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2.5px] rounded-r-full" style={{
                            height: '16px',
                            background: theme.accentGradient,
                          }} />
                        )}
                        <div className="flex-shrink-0 w-[26px] h-[26px] rounded-lg flex items-center justify-center transition-all duration-200" style={{
                          background: active ? theme.gradient : isHovered ? theme.primaryMedium : theme.primaryFaint,
                          color: active ? '#fff' : isHovered ? theme.primary : '#9ca3af',
                          boxShadow: active ? `0 3px 10px ${theme.primary}30` : isHovered ? `0 2px 6px ${theme.primary}12` : 'none',
                          transform: active ? 'scale(1.02)' : 'scale(1)',
                        }}>
                          {getIcon(service.icon, 14)}
                        </div>
                        <span className="truncate flex-1" style={{ letterSpacing: '0.01em' }}>{service.name}</span>
                      </Link>
                    </div>
                  </React.Fragment>
                );
              }

              // Service group (super main menu)
              const isOpen = openSuperMenu === service.key;
              const hasActive = isSuperMenuActive(service);
              const isHovered = hoveredItem === `super-${service.key}`;

              // Calculate total items for max-height animation
              let totalItemHeight = 0;
              if (service.items) {
                service.items.forEach(item => {
                  totalItemHeight += 34;
                  if (item.children && openSubmenu === item.menuKey) {
                    totalItemHeight += item.children.length * 32 + 6;
                  }
                });
                totalItemHeight += 16;
              }

              // Show labeled section divider between different service categories
              const showDivider = prevService && (
                (prevService.type === 'link' && service.type !== 'link') ||
                (service.type !== 'link' && prevService.type !== 'link' && service.themeKey !== prevService.themeKey)
              );

              return (
                <React.Fragment key={service.key}>
                  {showDivider && renderSectionDivider(
                    service.themeKey === 'whatsapp' ? 'WhatsApp' :
                    service.themeKey === 'rcs' ? 'RCS' :
                    service.themeKey === 'email' ? 'Email' :
                    service.themeKey === 'messaging' ? 'Services' :
                    'More',
                    theme
                  )}

                  <div className="service-section" style={{
                    background: isOpen ? theme.sectionBg : 'transparent',
                    border: isOpen ? `1.5px solid ${theme.borderLine}` : '1.5px solid transparent',
                    padding: isOpen ? '3px' : '0px',
                    transition: 'all 0.25s ease',
                    boxShadow: isOpen ? `0 2px 12px ${theme.primary}15, inset 0 0 0 1px ${theme.primary}08` : 'none',
                  }}>
                    {/* Super Main Menu Header */}
                    <button
                      onClick={() => toggleSuperMenu(service.key)}
                      onMouseEnter={() => setHoveredItem(`super-${service.key}`)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className="sidebar-item w-full group relative flex items-center gap-2.5 px-2.5 rounded-lg font-semibold"
                      style={{
                        height: '38px',
                        color: isOpen || hasActive ? theme.primary : isHovered ? '#1e1b4b' : '#111827',
                        background: isOpen ? theme.primaryMedium : hasActive ? theme.primaryLight : isHovered ? theme.primaryFaint : 'transparent',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {/* Color indicator dot */}
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r-full transition-all duration-200" style={{
                        width: isOpen || hasActive ? '3px' : '0px',
                        height: isOpen ? '20px' : hasActive ? '14px' : '0px',
                        background: theme.gradient,
                        opacity: isOpen || hasActive ? 1 : 0,
                      }} />

                      <div className="flex-shrink-0 w-[28px] h-[28px] rounded-lg flex items-center justify-center transition-all duration-200" style={{
                        background: isOpen || hasActive ? theme.gradient : isHovered ? theme.primaryMedium : theme.primaryFaint,
                        color: isOpen || hasActive ? '#fff' : isHovered ? theme.primary : '#94a3b8',
                        boxShadow: isOpen || hasActive ? `0 3px 10px ${theme.primary}35` : isHovered ? `0 2px 6px ${theme.primary}15` : 'none',
                        transform: isOpen || hasActive ? 'scale(1.02)' : 'scale(1)',
                      }}>
                        {getIcon(service.icon, 14)}
                      </div>
                      <span className="text-[13.5px] truncate flex-1 text-left" style={{ letterSpacing: '0.01em' }}>{service.name}</span>
                      {service.comingSoon && (
                        <span className="px-1.5 py-0.5 text-[8px] font-bold rounded-full flex-shrink-0" style={{
                          background: theme.primaryLight,
                          color: theme.primary,
                          border: `1px solid ${theme.borderLine}`,
                          letterSpacing: '0.03em',
                        }}>
                          SOON
                        </span>
                      )}
                      <Icons.ChevronDown className="w-3.5 h-3.5 flex-shrink-0 transition-all duration-200" style={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        opacity: isOpen ? 0.7 : 0.3,
                        color: isOpen ? theme.primary : undefined,
                      }} />
                    </button>

                    {/* Service items */}
                    <div className="super-menu-content" style={{
                      maxHeight: isOpen ? `${totalItemHeight + 200}px` : '0px',
                      opacity: isOpen ? 1 : 0,
                      overflow: 'hidden',
                    }}>
                      <div className="pt-0.5 pb-1 pl-1 pr-0.5">
                        {service.items?.map((item) => {
                          if (item.children) {
                            return renderMenuGroup(item, theme);
                          }
                          return renderDirectItem(item, theme);
                        })}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </nav>

          {/* User Profile Section */}
          <div className="p-2.5" style={{ borderTop: '1px solid #e8eaed' }}>
            {/* Balance card (for regular users) */}
            {(user?.balance !== undefined || balanceData?.available_balance !== undefined) && (
              <div
                className="mb-2 px-3 py-2.5 rounded-xl transition-all duration-150 cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(6, 182, 212, 0.04) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                }}
                onClick={handleBalanceClick}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.1)'; e.currentTarget.style.boxShadow = 'none'; }}
                title="Click to refresh balance"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Icons.Wallet className="w-3.5 h-3.5" style={{ color: '#059669' }} />
                    <span className="text-[11px] text-gray-500 font-medium">Balance</span>
                    <Icons.RefreshCw className={`w-2.5 h-2.5 text-gray-400 ${balanceRefreshing ? 'animate-spin' : ''}`} />
                  </div>
                  <span className="text-[13px] font-bold" style={{ color: '#059669' }}>
                    {balanceRefreshing ? '...' : formatIndianCurrency(balanceData?.available_balance || user.balance || 0)}
                  </span>
                </div>
                {balanceData?.sms_capacity && balanceData.sms_capacity > 0 && (
                  <div className="flex items-center justify-between mt-1.5 pt-1.5" style={{ borderTop: '1px solid rgba(16, 185, 129, 0.08)' }}>
                    <span className="text-[10px] text-gray-400 font-medium">SMS Capacity</span>
                    <span className="text-[12px] font-bold" style={{ color: '#4f46e5' }}>
                      {balanceRefreshing ? '...' : balanceData.sms_capacity.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* User info + logout */}
            <div className="flex items-center gap-2.5 px-2 py-2">
              <div className="relative flex-shrink-0">
                <div className="rounded-lg flex items-center justify-center text-white font-bold text-xs" style={{
                  width: '34px',
                  height: '34px',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)',
                  boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)',
                }}>
                  {user?.full_name?.[0] || user?.username?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 border-[1.5px] border-white rounded-full" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-semibold text-gray-800 truncate leading-tight">
                  {user?.full_name || user?.username}
                </p>
                <p className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">
                  {user?.role?.replace('_', ' ').toUpperCase()}
                </p>
              </div>
              <button
                onClick={logout}
                className="flex-shrink-0 w-[32px] h-[32px] rounded-lg flex items-center justify-center transition-all duration-150"
                style={{ color: '#94a3b8', background: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; e.currentTarget.style.color = '#ef4444'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                title="Logout"
              >
                <Icons.LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceSidebar;
